import {EventMessage, EventPayloads, EventType} from "./IntegrationEventTypes.ts";
import {isPayloadForType} from "./IntegrationEventGuards.ts";

type Handler<T extends EventType> = (message: EventMessage<T>) => void;

export class IntegrationEventRouter {
    private handlers = new Map<EventType, Set<Handler<EventType>>>();

    public on<T extends EventType>(type: T, handler: Handler<T>): () => void {
        const existing = this.handlers.get(type);
        const handlers = existing ?? new Set<Handler<EventType>>();
        handlers.add(handler as Handler<EventType>);
        if (!existing) {
            this.handlers.set(type, handlers);
        }
        return () => {
            handlers.delete(handler as Handler<EventType>);
        };
    }

    public dispatch<T extends EventType>(type: T, payload: unknown): boolean {
        if (!isPayloadForType(type, payload)) {
            console.warn(`[IntegrationEventRouter] Invalid payload for ${type}`, payload);
            return false;
        }

        const handlers = this.handlers.get(type);
        if (!handlers || handlers.size === 0) {
            console.warn(`[IntegrationEventRouter] Unhandled event ${type}`, payload);
            return false;
        }

        const message: EventMessage<T> = {
            type,
            payload: payload as EventPayloads[T],
        };

        for (const handler of handlers) {
            try {
                handler(message as EventMessage<EventType>);
            } catch (error) {
                console.error(`[IntegrationEventRouter] Handler failed for ${type}`, error);
            }
        }

        return true;
    }

    public clear(): void {
        this.handlers.clear();
    }
}
